import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import Favorite from '@jwp/ott-theme/assets/icons/favorite.svg?react';
import FavoriteBorder from '@jwp/ott-theme/assets/icons/favorite_border.svg?react';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import Button from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import Icon from '../../components/Icon/Icon';
import { useTheme } from '../../../../../ThemeContext';
import { Auth } from 'aws-amplify';
import { setLocalStorage } from '@jwp/ott-common/src/utils/localstorage';
import { useLocation, useNavigate } from 'react-router';
import { modalURLFromLocation } from '../../utils/location';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

type Props = {
  item: PlaylistItem;
};

const FavoriteButton: React.VFC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const { universalStyles } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const favoritesController = getModule(FavoritesController);
  const { isFavoriteEnable, isCognitoEnabled, isCustomApiEnabled, isShowLoginButton, isShowRegisterButton, isAuthenticationEnabled } = useAppConfig();
  const { buttonIconOnly, buttonLabelOnly, buttonLabelWithIcon } = useUIControls();
  const isLoggedIn = !!useAccountStore(({ user }) => user);

  const { isFavorite, clearWarning, warning } = useFavoritesStore((state) => ({
    isFavorite: !!item && state.hasItem(item),
    clearWarning: state.clearWarning,
    warning: state.warning,
  }));

  const onFavoriteButtonClick = useCallback(async () => {
    if (!isLoggedIn && isCognitoEnabled && isCustomApiEnabled) {
      await setLocalStorage('redirectURL', location.pathname);
      await Auth.federatedSignIn();
    } else {
      if (!isLoggedIn) {
        navigate(modalURLFromLocation(location, 'create-account'));
      } else {
        await favoritesController.toggleFavorite(item);
        sendGAEvent('add_to_wishlist', {
          content_id: item.mediaid,
          content_title: item.title,
        });
      }
    }
  }, [item, favoritesController]);

  useEffect(() => {
    // clear warning on unmount (probably by navigating away from the page using the back button)
    return () => clearWarning();
  }, [clearWarning]);

  return (
    <>
      {isFavoriteEnable && (isShowLoginButton || isShowRegisterButton) && isAuthenticationEnabled ? (
        <>
          <Button
            label={buttonLabelOnly || buttonLabelWithIcon ? t('video:favorite') : undefined}
            // color="videoDetailButton"
            variant="contained"
            size="exlarge"
            aria-label={t('video:favorite')}
            startIcon={buttonIconOnly || buttonLabelWithIcon ? isFavorite ? <Icon icon={Favorite} /> : <Icon icon={FavoriteBorder} /> : undefined}
            onClick={onFavoriteButtonClick}
            aria-pressed={isFavorite}
            color={isFavorite ? 'primary' : 'default'}
            fullWidth={breakpoint < Breakpoint.md}
            // style={universalStyles.favoriteButtonBackground}
            textStyle={universalStyles.favoriteAndShareButtonText}
          />
          <Alert open={warning !== null} message={warning} onClose={clearWarning} />
        </>
      ) : null}
    </>
  );
};

export default FavoriteButton;
